import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import emailjs from '@emailjs/browser';
import {
  Box,
  Button,
  Typography,
  TextField,
  FormHelperText,
  Paper,
  Card,
  CircularProgress,
  Alert,
  Fade,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import serviceTypes from '../../config/services';
import budgetRanges from '../../config/budgetRanges';
import { EMAILJS_CONFIG } from '../../config/emailjs';

// Country codes for phone numbers
interface CountryCode {
  code: string;
  name: string;
  flag: string;
}

const countryCodes: CountryCode[] = [
  { code: '+852', name: 'Hong Kong', flag: '🇭🇰' },
  { code: '+86', name: 'China', flag: '🇨🇳' },
  { code: '+1', name: 'United States', flag: '🇺🇸' },
  { code: '+44', name: 'United Kingdom', flag: '🇬🇧' },
  { code: '+81', name: 'Japan', flag: '🇯🇵' },
  { code: '+82', name: 'South Korea', flag: '🇰🇷' },
  { code: '+65', name: 'Singapore', flag: '🇸🇬' },
  { code: '+61', name: 'Australia', flag: '🇦🇺' },
  { code: '+886', name: 'Taiwan', flag: '🇹🇼' },
];

interface FormValues {
  service: string;
  budget: string;
  countryCode: string;
  phone: string;
}

const StepOrderForm = () => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language.startsWith('zh') ? 'zh' : 'en';
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    // Initialize EmailJS with your public key
    emailjs.init(EMAILJS_CONFIG.PUBLIC_KEY);
    
    // Debug environment variables
    console.log('EmailJS Environment Variables Status (StepOrderForm):', {
      'SERVICE_ID': Boolean(EMAILJS_CONFIG.SERVICE_ID),
      'TEMPLATE_ID': Boolean(EMAILJS_CONFIG.TEMPLATE_ID),
      'PUBLIC_KEY': Boolean(EMAILJS_CONFIG.PUBLIC_KEY),
      'ADMIN_EMAIL': EMAILJS_CONFIG.ADMIN_EMAIL
    });
    
    // Check for missing required configs
    if (!EMAILJS_CONFIG.SERVICE_ID || !EMAILJS_CONFIG.TEMPLATE_ID || !EMAILJS_CONFIG.PUBLIC_KEY) {
      console.warn('EmailJS configuration incomplete - emails will not be sent. Check your environment variables.');
    }
  }, []);

  // Validation schema
  const validationSchema = Yup.object({
    service: Yup.string().required(t('quickOrder.validation.servicesRequired')),
    budget: Yup.string().required(t('quickOrder.validation.budgetRequired')),
    countryCode: Yup.string().required(t('quickOrder.validation.countryCodeRequired')),
    phone: Yup.string()
      .required(t('quickOrder.validation.phoneRequired'))
      .when('countryCode', {
        is: '+852',
        then: (schema) => schema.matches(/^[0-9]{8}$/, t('quickOrder.validation.hkPhoneInvalid')),
        otherwise: (schema) => schema.matches(/^[0-9]{6,12}$/, t('quickOrder.validation.phoneInvalid'))
      }),
  });

  // Form initial values
  const initialValues: FormValues = {
    service: '',
    budget: '',
    countryCode: '+852', // Default to Hong Kong
    phone: '',
  };

  // Initialize formik
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      // We don't use this directly - using handleSubmitForm instead
      console.log('Formik onSubmit called with:', values);
    },
  });

  // Form submission handler - completely separate from Formik's submit
  const handleSubmitForm = async () => {
    // First check if the phone input is valid
    let isValid = true;
    
    if (!formik.values.phone) {
      formik.setFieldError('phone', t('quickOrder.validation.phoneRequired'));
      isValid = false;
    } else if (formik.values.countryCode === '+852' && !/^[0-9]{8}$/.test(formik.values.phone)) {
      formik.setFieldError('phone', t('quickOrder.validation.hkPhoneInvalid'));
      isValid = false;
    } else if (!/^[0-9]{6,12}$/.test(formik.values.phone)) {
      formik.setFieldError('phone', t('quickOrder.validation.phoneInvalid'));
      isValid = false;
    }
    
    if (!isValid) return;
    
    // If validation passes, submit the form
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Get the service and budget in readable format
      const selectedService = serviceTypes.find(s => s.id === formik.values.service);
      const serviceName = selectedService ? selectedService.name[currentLanguage] : formik.values.service;
      
      const selectedBudget = budgetRanges.find(b => b.id === formik.values.budget);
      const budgetText = selectedBudget ? selectedBudget.range[currentLanguage] : formik.values.budget;
      
      const values = formik.values;
      console.log('Form submitted:', values);
      
      // Send email to admin using EmailJS
      try {
        console.log('Sending email notification to admin');
        
        // Check if admin email is available, otherwise use a fallback
        const adminEmail = EMAILJS_CONFIG.ADMIN_EMAIL || 'blackrc3636@gmail.com';
        
        if (!adminEmail) {
          throw new Error('Admin email is not configured');
        }
        
        console.log('Using admin email:', adminEmail);

        const emailParams = {
          to_email: adminEmail,        // Standard format
          to_name: 'Admin',            // Add recipient name
          recipient: adminEmail,       // Alternative format
          email: adminEmail,           // Another alternative format
          from_name: 'Gamifly Website Form',
          subject: 'New Home Page Quick Form Submission',
          service: serviceName,
          services: serviceName,       // Try alternative parameter name
          service_type: serviceName,   // Try another alternative
          budget: budgetText,
          phone: `${values.countryCode} ${values.phone}`,
          message_html: `
            <h2>New Form Submission</h2>
            <p><strong>Service:</strong> ${serviceName}</p>
            <p><strong>Budget:</strong> ${budgetText}</p>
            <p><strong>Phone:</strong> ${values.countryCode} ${values.phone}</p>
          `,
        };

        console.log('Email parameters:', emailParams);

        // Debug which parameters are actually valid
        console.log('EmailJS parameters validity check:');
        Object.keys(emailParams).forEach(key => {
          console.log(`${key}: ${Boolean(emailParams[key as keyof typeof emailParams])}`);
        });

        const response = await emailjs.send(
          EMAILJS_CONFIG.SERVICE_ID,
          EMAILJS_CONFIG.TEMPLATE_ID,
          emailParams
        );
        
        console.log('Email sent successfully:', response);
      } catch (emailError: any) {
        console.error('Error sending email notification:', emailError);
        console.error('Error details:', emailError.text);
        // Continue with the form submission even if email fails
      }
      
      // Simulating API call for form submission - in real app, this would be a Firebase function call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setSubmitSuccess(true);
      formik.resetForm();
      setCurrentStep(0);
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError(t('quickOrder.submitError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Previous step handler
  const handlePrevStep = () => {
    setCurrentStep(prevStep => Math.max(0, prevStep - 1));
  };
  
  // Handle service selection
  const handleServiceSelect = (serviceId: string) => {
    formik.setFieldValue('service', serviceId);
    // Immediately go to next step without setTimeout
    setCurrentStep(1);
  };

  // Handle budget selection
  const handleBudgetSelect = (budgetId: string) => {
    formik.setFieldValue('budget', budgetId);
    // Immediately go to next step without setTimeout
    setCurrentStep(2);
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Service type selection
        return (
          <Fade in={true} timeout={500}>
            <Box>
              <Typography variant="h5" gutterBottom align="center" sx={{ mb: 4 }} color="common.white">
                {t('quickOrder.serviceTypeLabel')}
              </Typography>
              
              <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 1, sm: 2 } }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center' }}>
                  {serviceTypes.map((service) => (
                    <Box key={service.id} sx={{ width: { xs: '100%', sm: '45%', md: '30%' } }}>
                      <Card 
                        sx={{ 
                          height: 200,
                          display: 'flex',
                          flexDirection: 'column',
                          borderColor: formik.values.service === service.id ? 'primary.main' : 'transparent',
                          borderWidth: 2,
                          borderStyle: 'solid',
                          transition: 'all 0.3s ease',
                          transform: formik.values.service === service.id ? 'translateY(-5px)' : 'none',
                          boxShadow: formik.values.service === service.id 
                            ? '0 8px 16px rgba(74, 144, 226, 0.3)'
                            : '0 4px 8px rgba(0, 0, 0, 0.2)',
                          '&:hover': {
                            borderColor: 'primary.main',
                            transform: 'translateY(-5px)',
                            boxShadow: '0 8px 16px rgba(74, 144, 226, 0.3)',
                          }
                        }}
                      >
                        <Box
                          component="button"
                          onClick={() => handleServiceSelect(service.id)}
                          sx={{ 
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: 3,
                            position: 'relative',
                            width: '100%',
                            cursor: 'pointer',
                            background: 'none',
                            border: 'none',
                            textAlign: 'left',
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)' }
                          }}
                        >
                          {formik.values.service === service.id && (
                            <CheckCircleIcon 
                              color="primary" 
                              sx={{ 
                                position: 'absolute',
                                top: 12,
                                right: 12,
                                fontSize: 28,
                                filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.3))'
                              }} 
                            />
                          )}
                          <Box 
                            sx={{ 
                              width: 70, 
                              height: 70, 
                              backgroundColor: 'rgba(74, 144, 226, 0.1)',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mb: 2,
                              transition: 'all 0.3s ease',
                              transform: formik.values.service === service.id ? 'scale(1.1)' : 'scale(1)',
                            }}
                          >
                            <img 
                              src={`/icons/${service.icon}.svg`} 
                              alt={service.name[currentLanguage as keyof typeof service.name]}
                              width={36}
                              height={36}
                              style={{ opacity: 0.9 }}
                            />
                          </Box>
                          <Typography 
                            variant="h6" 
                            component="h3" 
                            align="center"
                            color="common.white"
                            sx={{
                              mb: 1,
                              fontWeight: 600
                            }}
                          >
                            {service.name[currentLanguage as keyof typeof service.name]}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            color="common.white" 
                            align="center" 
                            sx={{ 
                              mt: 0.5,
                              lineHeight: 1.4,
                              height: 40,
                              overflow: 'hidden',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical'
                            }}
                          >
                            {service.description[currentLanguage as keyof typeof service.description]}
                          </Typography>
                        </Box>
                      </Card>
                    </Box>
                  ))}
                </Box>
              </Box>
              
              {formik.errors.service && (
                <FormHelperText error sx={{ mt: 2, textAlign: 'center' }}>
                  {formik.errors.service}
                </FormHelperText>
              )}
            </Box>
          </Fade>
        );

      case 1: // Budget selection
        return (
          <Fade in={true} timeout={500}>
            <Box>
              <Typography variant="h5" gutterBottom align="center" sx={{ mb: 4 }} color="common.white">
                {t('quickOrder.budgetLabel')}
              </Typography>
              
              <Box sx={{ maxWidth: 1000, mx: 'auto', px: { xs: 1, sm: 2 } }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center' }}>
                  {budgetRanges.map((budget) => (
                    <Box key={budget.id} sx={{ width: { xs: '100%', sm: '45%' } }}>
                      <Card 
                        sx={{ 
                          height: 100,
                          borderColor: formik.values.budget === budget.id ? 'primary.main' : 'transparent',
                          borderWidth: 2,
                          borderStyle: 'solid',
                          transition: 'all 0.3s ease',
                          transform: formik.values.budget === budget.id ? 'translateY(-5px)' : 'none',
                          boxShadow: formik.values.budget === budget.id 
                            ? '0 8px 16px rgba(74, 144, 226, 0.3)'
                            : '0 4px 8px rgba(0, 0, 0, 0.2)',
                          '&:hover': {
                            borderColor: 'primary.main',
                            transform: 'translateY(-5px)',
                            boxShadow: '0 8px 16px rgba(74, 144, 226, 0.3)',
                          }
                        }}
                      >
                        <Box
                          component="button"
                          onClick={() => handleBudgetSelect(budget.id)}
                          sx={{ 
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                            width: '100%',
                            cursor: 'pointer',
                            background: 'none',
                            border: 'none',
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)' }
                          }}
                        >
                          {formik.values.budget === budget.id && (
                            <CheckCircleIcon 
                              color="primary" 
                              sx={{ 
                                position: 'absolute',
                                top: 12,
                                right: 12,
                                fontSize: 28,
                                filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.3))'
                              }} 
                            />
                          )}
                          <Typography 
                            variant="h6" 
                            component="h3" 
                            align="center"
                            color="common.white"
                            sx={{
                              fontWeight: 600,
                              px: 2
                            }}
                          >
                            {budget.range[currentLanguage as keyof typeof budget.range]}
                          </Typography>
                        </Box>
                      </Card>
                    </Box>
                  ))}
                </Box>
              </Box>
              
              {formik.errors.budget && (
                <FormHelperText error sx={{ mt: 2, textAlign: 'center' }}>
                  {formik.errors.budget}
                </FormHelperText>
              )}
            </Box>
          </Fade>
        );

      case 2: // Phone number form
        return (
          <Fade in={true} timeout={500}>
            <Box>
              <Typography variant="h5" gutterBottom align="center" sx={{ mb: 4 }} color="common.white">
                {t('quickOrder.phoneLabel')}
              </Typography>
              <Box sx={{ maxWidth: 1000, mx: 'auto', px: { xs: 1, sm: 2 } }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center' }}>
                  <Box sx={{ width: { xs: '100%', sm: '45%' } }}>
                    <FormControl fullWidth>
                      <InputLabel id="country-code-label" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        {t('quickOrder.countryCodeLabel')}
                      </InputLabel>
                      <Select
                        labelId="country-code-label"
                        id="country-code"
                        value={formik.values.countryCode}
                        label={t('quickOrder.countryCodeLabel')}
                        onChange={(e) => formik.setFieldValue('countryCode', e.target.value)}
                        sx={{ color: 'common.white' }}
                      >
                        {countryCodes.map((code) => (
                          <MenuItem key={code.code} value={code.code}>
                            {code.flag} {code.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  <Box sx={{ width: { xs: '100%', sm: '45%' } }}>
                    <TextField
                      fullWidth
                      id="phone"
                      name="phone"
                      label={t('quickOrder.phoneLabel')}
                      value={formik.values.phone}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.phone && Boolean(formik.errors.phone)}
                      helperText={formik.touched.phone && formik.errors.phone}
                      InputLabelProps={{
                        sx: { color: 'rgba(255, 255, 255, 0.7)' }
                      }}
                      InputProps={{
                        sx: { color: 'common.white' },
                        startAdornment: (
                          <InputAdornment position="start" sx={{ color: 'common.white' }}>
                            {formik.values.countryCode}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={handleSubmitForm}
                    disabled={isSubmitting}
                    sx={{ minWidth: 250, py: 1.5, fontSize: '1.2rem', background: 'linear-gradient(45deg, #4A90E2 30%, #5C6BC0 90%)' }}
                  >
                    {isSubmitting ? <CircularProgress size={24} color="inherit" /> : t('quickOrder.submitButton')}
                  </Button>
                </Box>
                {formik.errors.phone && (
                  <FormHelperText error sx={{ mt: 2, textAlign: 'center' }}>
                    {formik.errors.phone}
                  </FormHelperText>
                )}
              </Box>
            </Box>
          </Fade>
        );

      default:
        return null;
    }
  };

  // Render the form
  return (
    <Box sx={{ mt: 6 }}>
      {!submitSuccess ? (
        <Box>
          <Paper 
            elevation={0}
            sx={{
              p: { xs: 2, sm: 4 },
              borderRadius: 4,
              backgroundColor: 'background.paper',
              maxWidth: 1200,
              mx: 'auto',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Progress indicator */}
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                mb: 4
              }}
            >
              {[0, 1, 2].map((step) => (
                <Box
                  key={step}
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: currentStep >= step ? 'primary.main' : 'rgba(255,255,255,0.2)',
                    mx: 0.5,
                    transition: 'all 0.3s ease',
                  }}
                />
              ))}
            </Box>

            {/* Step Content */}
            <Box>
              {renderStepContent()}
            </Box>

            {/* Navigation */}
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                mt: 4,
                pt: 2,
                borderTop: '1px solid rgba(255,255,255,0.1)'
              }}
            >
              {currentStep > 0 ? (
                <Button 
                  startIcon={<ArrowBackIcon />}
                  onClick={handlePrevStep}
                  variant="text"
                  color="inherit"
                >
                  {t('common.back')}
                </Button>
              ) : (
                <Box />
              )}
              
              {submitError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {submitError}
                </Alert>
              )}
            </Box>
          </Paper>
        </Box>
      ) : (
        <Fade in={true} timeout={500}>
          <Paper 
            elevation={0}
            sx={{
              p: { xs: 4, sm: 6 },
              borderRadius: 4,
              backgroundColor: 'background.paper',
              maxWidth: 800,
              mx: 'auto',
              textAlign: 'center',
            }}
          >
            <Box 
              sx={{ 
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: 'rgba(76, 175, 80, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3
              }}
            >
              <CheckCircleIcon color="success" sx={{ fontSize: 50 }} />
            </Box>
            <Typography variant="h4" component="h2" gutterBottom>
              {t('quickOrder.success.title')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 4 }}>
              {t('quickOrder.success.message')}
            </Typography>
            <Button
              variant="contained"
              onClick={() => setSubmitSuccess(false)}
              sx={{ 
                minWidth: 200,
                background: 'linear-gradient(45deg, #4A90E2 30%, #5C6BC0 90%)',
              }}
            >
              {t('quickOrder.success.newOrderButton')}
            </Button>
          </Paper>
        </Fade>
      )}
    </Box>
  );
};

export default StepOrderForm; 